import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import supabase from '@/lib/supabase';

interface VerifiedSite {
  id: number;
  name: string;
  domain: string;
  status: 'safe' | 'warning' | 'scam';
  report_count: number;
  verified_date: string;
  category: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

const STATUS_LABELS: Record<string, string> = {
  safe: '안전',
  warning: '주의',
  scam: '먹튀',
};

const STATUS_COLORS: Record<string, string> = {
  safe: 'bg-green-100 text-green-700',
  warning: 'bg-yellow-100 text-yellow-700',
  scam: 'bg-red-100 text-red-700',
};

const CATEGORIES = ['카지노', '토토', '슬롯', '파워볼', '경마', '기타'];

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  } catch {
    return dateStr;
  }
}

export default function VerifiedSitesSection() {
  const [sites, setSites] = useState<VerifiedSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'safe' | 'warning' | 'scam'>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<VerifiedSite | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const [form, setForm] = useState({
    name: '',
    domain: '',
    status: 'safe' as 'safe' | 'warning' | 'scam',
    category: '카지노',
    tags: '',
    verified_date: new Date().toISOString().split('T')[0],
  });

  const fetchSites = async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('verified_sites')
        .select('*')
        .order('updated_at', { ascending: false, nullsFirst: false });

      if (search.trim()) {
        const term = search.trim();
        query = query.or(`name.ilike.%${term}%,domain.ilike.%${term}%`);
      }

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      setSites((data || []) as VerifiedSite[]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(message || '검증 사이트 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, [statusFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchSites();
  };

  const openAddModal = () => {
    setEditingSite(null);
    setForm({
      name: '',
      domain: '',
      status: 'safe',
      category: '카지노',
      tags: '',
      verified_date: new Date().toISOString().split('T')[0],
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (site: VerifiedSite) => {
    setEditingSite(site);
    setForm({
      name: site.name,
      domain: site.domain,
      status: site.status,
      category: site.category,
      tags: (site.tags || []).join(', '),
      verified_date: site.verified_date || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!form.name.trim() || !form.domain.trim()) {
      setFormError('사이트명과 도메인은 필수 입력 항목입니다.');
      return;
    }

    const tagArray = form.tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    setActionLoading(true);
    try {
      if (editingSite) {
        const { error: updateError } = await supabase
          .from('verified_sites')
          .update({
            name: form.name.trim(),
            domain: form.domain.trim(),
            status: form.status,
            category: form.category,
            tags: tagArray,
            verified_date: form.verified_date || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingSite.id);

        if (updateError) throw updateError;

        setSites((prev) =>
          prev.map((s) =>
            s.id === editingSite.id
              ? { ...s, ...form, tags: tagArray, updated_at: new Date().toISOString() }
              : s
          )
        );
      } else {
        const { data, error: insertError } = await supabase
          .from('verified_sites')
          .insert({
            name: form.name.trim(),
            domain: form.domain.trim(),
            status: form.status,
            category: form.category,
            tags: tagArray,
            report_count: 0,
            verified_date: form.verified_date || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (insertError) throw insertError;

        if (data) {
          setSites((prev) => [data as VerifiedSite, ...prev]);
        }
      }

      setShowModal(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setFormError(message || '저장 중 오류가 발생했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (site: VerifiedSite) => {
    if (!confirm(`"${site.name}" 사이트를 정말 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;

    setActionLoading(true);
    try {
      const { error: deleteError } = await supabase
        .from('verified_sites')
        .delete()
        .eq('id', site.id);

      if (deleteError) throw deleteError;

      setSites((prev) => prev.filter((s) => s.id !== site.id));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">검증 사이트 관리</h2>
          <p className="text-sm text-foreground-500">사이트 검증 정보를 추가하고 수정/삭제합니다</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
        >
          + 새 검증 등록
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {(['all', 'safe', 'warning', 'scam'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                statusFilter === f
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {f === 'all' ? `전체 (${sites.length})` : `${STATUS_LABELS[f]}`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="사이트명 또는 도메인 검색"
              className="px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500 w-56"
            />
            <button
              type="submit"
              className="px-3 py-2 text-sm font-medium bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              검색
            </button>
          </form>
          <button
            onClick={fetchSites}
            className="px-3 py-2 text-sm font-medium bg-white border border-foreground-200 hover:bg-background-50 text-foreground-700 rounded-md transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line" />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <p className="text-sm text-red-600">{error}</p>
          <button onClick={fetchSites} className="ml-auto text-xs text-red-600 hover:text-red-700 font-medium whitespace-nowrap cursor-pointer">
            다시 시도
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 bg-white rounded-lg border border-foreground-200">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-foreground-500">검증 사이트 목록을 불러오는 중...</p>
        </div>
      ) : sites.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-lg border border-foreground-200">
          <div className="w-14 h-14 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-shield-check-line text-2xl text-foreground-400" />
          </div>
          <h3 className="text-base font-bold text-foreground-950 mb-1">
            {search.trim() || statusFilter !== 'all' ? '검색 결과가 없습니다' : '등록된 검증 사이트가 없습니다'}
          </h3>
          <p className="text-sm text-foreground-500">
            {search.trim() || statusFilter !== 'all'
              ? '다른 검색어나 필터로 다시 시도해보세요.'
              : '새 검증 등록 버튼을 눌러 첫 번째 사이트를 추가해보세요.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-foreground-200 bg-background-100">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">사이트명</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">도메인</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">분류</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">상태</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">신고</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">검증일</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">태그</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">관리</th>
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => (
                  <tr key={site.id} className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50">
                    <td className="px-4 py-3 text-sm font-medium text-foreground-950 whitespace-nowrap">
                      {site.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-500 whitespace-nowrap">
                      {site.domain}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded whitespace-nowrap">
                        {site.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[site.status] || 'bg-foreground-200 text-foreground-500'}`}>
                        {STATUS_LABELS[site.status] || site.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-600 text-center whitespace-nowrap">
                      {(site.report_count || 0).toLocaleString()}건
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-500 whitespace-nowrap">
                      {formatDate(site.verified_date)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap max-w-[200px]">
                        {(site.tags || []).slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-1.5 py-0.5 rounded whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                        {(site.tags || []).length > 3 && (
                          <span className="text-xs text-foreground-400">
                            +{(site.tags || []).length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <Link
                          to={`/site/${site.id}`}
                          className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
                        >
                          보기
                        </Link>
                        <button
                          onClick={() => openEditModal(site)}
                          className="text-xs text-foreground-500 hover:text-foreground-700 font-medium whitespace-nowrap cursor-pointer"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => handleDelete(site)}
                          disabled={actionLoading}
                          className="text-xs text-red-500 hover:text-red-600 font-medium whitespace-nowrap disabled:opacity-50 cursor-pointer"
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowModal(false)}
          />
          <div className="relative bg-white rounded-lg border border-foreground-200 w-full max-w-lg mx-4 shadow-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-foreground-200">
              <h3 className="text-base font-bold text-foreground-950">
                {editingSite ? '검증 사이트 수정' : '새 검증 사이트 등록'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <form onSubmit={handleSave} className="px-6 py-4">
              {formError && (
                <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500 shrink-0" />
                  <p className="text-sm text-red-600">{formError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    사이트명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="예: 온카지노"
                    className="w-full px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    도메인 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.domain}
                    onChange={(e) => setForm({ ...form, domain: e.target.value })}
                    placeholder="예: example.com"
                    className="w-full px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">검증 상태</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value as 'safe' | 'warning' | 'scam' })}
                      className="w-full px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500 cursor-pointer"
                    >
                      <option value="safe">안전</option>
                      <option value="warning">주의</option>
                      <option value="scam">먹튀</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">분류</label>
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500 cursor-pointer"
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">검증일</label>
                  <input
                    type="date"
                    value={form.verified_date}
                    onChange={(e) => setForm({ ...form, verified_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500 cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">
                    태그 <span className="text-foreground-400 font-normal">(쉼표로 구분)</span>
                  </label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    placeholder="예: 검증완료, 신규, 추천"
                    className="w-full px-3 py-2 text-sm border border-foreground-200 rounded-md bg-white focus:outline-none focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t border-foreground-200">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {actionLoading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {editingSite ? '수정 저장' : '등록'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-white border border-foreground-200 hover:bg-background-50 text-foreground-700 text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}